import React, { FC, useState } from 'react'
import PeriodSelector from './PeriodSelector'
import { RepastPeriodItem, RepastPeriodRule } from '@/pages/rule/utils/types'
import {  i18nClient, useTranslation } from '@sailor/i18n-web'
import Button from '@roo/roo/Button'
import CheckBox from '@roo/roo/CheckBox'
import Modal from '@roo/roo/Modal'
import Radio from '@roo/roo/Radio'
import {  warningToast } from '@/common/utils/toast'
import { isEqualString } from '@/common/utils'
import styles from './index.module.less'
import { RepastWorkDayEnum } from '../../utils/types'
import { isRtl } from '@/common/utils/i18n'

const CheckBoxGroup = CheckBox.Group

const RadioGroup = Radio.Group
interface PeriodRuleProps {
  value: RepastPeriodRule
  onChange: (val: RepastPeriodRule) => void
}

const weekTextKeyMap = {
  '1': 'e_pc_monday',
  '2': 'e_pc_tuesday',
  '3': 'e_pc_wednesday',
  '4': 'e_pc_thursday',
  '5': 'e_pc_friday',
  '6': 'e_pc_saturday',
  '7': 'e_pc_sunday',
}

const workDayTextKeyMap = {
  '1': 'e_pc_weekday', // 工作日
  '2': 'e_pc_non_weekday', // 非工作日
}

enum RepastTimeTypeEnum {
  AllDay = 1, // 全天
  SpecificPeriod = 2 // 特定时段
}


const PeriodRule: FC<PeriodRuleProps> = (props) => {
  const { value, onChange } = props
  const { t } = useTranslation()
  const { repastPeriodList = [], checkRepastPeriod } = value
  const limitVal = checkRepastPeriod ?  'LIMIT' : 'NO_LIMIT'
  const [editRuleIndex, setEditRuleIndex] = useState<number | undefined>()

  function handlePeriodItemChange (editIndex: number, data?: RepastPeriodItem) {
    setEditRuleIndex(undefined)
    let newList = [...repastPeriodList]
    // @ts-ignore  允许置空,表示删除操作
    newList[editIndex] = data
    props.onChange({
      ...value,
      repastPeriodList: newList.filter(item => item),
    })
  }

  return (
    <div >
      <RadioGroup value={limitVal} onChange={(val) => props.onChange({
        ...value,
        checkRepastPeriod: val === 'LIMIT',
      })}>
        <Radio value={'NO_LIMIT'}>{t('e_pc_no_limit', '不限制')}</Radio>
        <Radio value="LIMIT">{t('e_pc_custom', '自定义')}</Radio>
      </RadioGroup>
      {checkRepastPeriod && (
        <div>
          {Number(editRuleIndex) >= 0 &&  (
            <PeridRuleItemModal
              value={repastPeriodList[editRuleIndex as number]}
              onClose={() => setEditRuleIndex(undefined)}
              onConfirm={(newItem) => {
                console.log(newItem, 'NEWITEM')
                if (newItem.repastTimeList?.length) {// 过滤空时间段
                  newItem.repastTimeList = newItem.repastTimeList.filter(item => item.startTime && item.endTime)
                }

                handlePeriodItemChange(editRuleIndex as number, newItem)
              }}   />
          )}
          <PeridRuleListRender data={repastPeriodList} onEdit={(index:number) => setEditRuleIndex(index)} onDelete={(index) => handlePeriodItemChange(index, undefined)}/>
          <Button  icon="plus"  type="hollow" size="mini" style={{ marginTop: '12px' }} onClick={() => setEditRuleIndex(repastPeriodList.length)} >{t('e_pc_add', '添加')}</Button>
        </div>
      )}
    </div>
  )
}

const defaultVal = {
  repastDateType: RepastWorkDayEnum.WORK_DAY, // 用餐日期类型（1-星期，2-工作日）
  weekList: [1, 2, 3, 4, 5, 6, 7], // 星期列表
  workdayTypeList: [1, 2], // 工作日类型列表
  repastTimeList: [
    { startTime: '00:00', endTime: '23:59' }, // 用餐时间列表
  ],
  repastTimeType: 1, // 用餐时间类型（1-全天，2-特定时段）
}

function PeridRuleItemModal (props: {
  value?: RepastPeriodItem,
  onConfirm: (val: RepastPeriodItem) => void
  onClose: () => void
}) {
  const { t } = useTranslation()

  const [modalVal, setModalVal] = useState(props.value || defaultVal)


  const isWorkDayType = isEqualString(modalVal.repastDateType, RepastWorkDayEnum.WORK_DAY)
  const dateOptions = Object.entries(isWorkDayType ? workDayTextKeyMap : weekTextKeyMap).map(([value, label]) => ({ value: Number(value), label }))
  const effectPeriodKeyName: 'weekList' | 'workdayTypeList' = isWorkDayType ? 'workdayTypeList' : 'weekList'

  const isNew = !props.value


  return (
    <Modal
      title={isNew ? t('e_pc_add_period', '添加时段') : t('e_pc_edit_period', '编辑时段')}
      visible
      cancelText={t('e_pc_cancel_button', 'Cancel')}
      confirmText={t('e_pc_confirm_button', 'Confirm')}
      onCancel={props.onClose}
      onConfirm={() => {
        props.onConfirm(modalVal)
        props.onClose()
      }}
    >
      <h4>{t('e_pceffective_period', '生效周期')}</h4>
      <RadioGroup
        value={String(modalVal.repastDateType)}
        onChange={(val) => setModalVal({
          ...modalVal,
          weekList: modalVal?.weekList?.length ? modalVal?.weekList : defaultVal.weekList, // 防止空值
          workdayTypeList: modalVal?.workdayTypeList?.length ? modalVal?.workdayTypeList : defaultVal.workdayTypeList, // 防止空值
          repastDateType: Number(val),
        })}
      >
        <Radio value={'2'}>{t('e_pc_weekday_holiday', '工作日/非工作日')}</Radio>
        <Radio value={'1'}>{t('e_pc_week', '星期')}</Radio>
      </RadioGroup>
      <CheckBoxGroup
        value={modalVal[effectPeriodKeyName] || []}
        onChange={(newlist: number[]) => {
          if (newlist.length === 0) {
            warningToast(t('e_pc_period_required', '生效周期不能为空'))
            return
          }
          setModalVal({ ...modalVal, [effectPeriodKeyName]: newlist })
        }}
      >
        <div style={{ paddingTop: '12px' }}>
          {dateOptions?.map((item) => (
            <CheckBox value={item?.value} type="brand" key={item.value}> {t(item.label)} </CheckBox>
          ))}
        </div>
      </CheckBoxGroup>
      <h4>{t('e_pceffective_period33g', '生效时段')}</h4>
      <RadioGroup value={String(modalVal.repastTimeType)} onChange={(val) => setModalVal({ ...modalVal, repastTimeType: Number(val) })}>
        <Radio value={'2'}>{t('e_pc_specific_period', '特定时段')}</Radio>
        <Radio value={'1'}>{t('e_pc_all_day', '全天')}</Radio>
      </RadioGroup>
      {
        isEqualString(modalVal.repastTimeType, RepastTimeTypeEnum.SpecificPeriod) && (
          <PeriodSelector
            onChange={(repastTimeList: {startTime: string, endTime: string}[]) => {setModalVal({ ...modalVal, repastTimeList })}}
            value={modalVal.repastTimeList}
          />
        )
      }
    </Modal>
  )
}

export function PeridRuleListRender (props: {
  data:RepastPeriodItem[],
  onEdit: (index: number) => void
  onDelete: (index: number) => void
}) {
  const data = props.data || []
  const timeTextList = getPeridTimeText(data).map((item, index) => ({ ...item, index }))
  return (
    <div>
      {timeTextList.map((item) => (
        <div className={styles['time-card']}>
          <div>
            <div>
              {item.weekText}
            </div>
            <div>
              {item.timeText}
            </div>
          </div>
          <div>
            {props.onEdit && (<Button type="brand-text" onClick={() => props.onEdit(item.index)} icon="edit" />)}
            &ensp;&ensp;
            {props.onDelete && (<Button type="brand-text" onClick={() => props.onDelete(item.index)} icon="trash-o" ></Button>)}
          </div>
        </div>
      ))}
    </div>
  )
}

export function getPeridTimeText (data: RepastPeriodItem[]) {
  const splitSign = i18nClient.getText('e_pc_corporate_uniform_delimiter') || '、'
  return (
    data.map((item) => {
      const weekText =  isEqualString(item.repastDateType, RepastWorkDayEnum.WORK_DAY)
        ? item.workdayTypeList?.map((week) => i18nClient.getText(workDayTextKeyMap[String(week)])).join(splitSign)
        : item.weekList?.map((week) => i18nClient.getText(weekTextKeyMap[String(week)])).join(splitSign)
      const timeText = isEqualString(item.repastTimeType, RepastTimeTypeEnum.AllDay)
        ? i18nClient.getText('e_pc_all_day', '全天')
        : item.repastTimeList?.map((time) => isRtl ? `${time.endTime}-${time.startTime}` : `${time.startTime}-${time.endTime}`).join(splitSign)

      return { timeText, weekText }
    })
  )
}

export default PeriodRule